import {useContext, useEffect, useRef, useState} from "react";
import {ToastContext} from "Toast";
import {useMessages} from "i18n";
import {useLoginModal} from "login/LoginModalProvider";
import type {CouponClaimActions} from "offers/ladder/CouponClaimDialog";
import {paymentAdmission} from "pay/withdrawalRight";
import {useEmbeddedSubscriptionCheckout} from "pay/useEmbeddedSubscriptionCheckout";
import type {CheckoutDocumentRead} from "pay/checkoutDocument";
import {useUser} from "UserProvider";
import {limitWallCheckout, resumeLimitWallCheckout, type LimitWallPresentation} from "./limitWallOffer";

/**
 * The limit-wall claim, satisfying {@link CouponClaimActions} so the coupon dialog needs no limit-wall
 * branch (kitecorp/transferify-api-service#2206), the same one-view-several-adapters split the promo link
 * uses (`offers/promo/usePromoClaim`), and for the same sign-in reasons: the reader may arrive signed out.
 *
 * What the press starts is `./limitWallOffer`'s `limitWallCheckout`, asked at the moment the checkout
 * starts rather than at the press, through refs a render keeps fresh.
 *
 * <b>A refusal is never retried.</b> The api answers `error.pay.offerLadderTokenInvalid` for every reason
 * it will not attach the email's code; the embedded checkout reports that as `offerRefused`, and this hands
 * it to the page, which withdraws the coupon and re-reads the link to show the full price or the expired
 * notice. No funnel step is reported from here: the api witnesses this surface itself.
 */
export function useLimitWallClaim(presentation: LimitWallPresentation, token: string | null,
	activeVisit: () => boolean, onClose: () => void, onRefused: () => void): CouponClaimActions {
	const inPage = useEmbeddedSubscriptionCheckout();
	const [acknowledged, setAcknowledged] = useState(false);
	const [user] = useUser();
	const userRef = useRef(user);
	userRef.current = user;
	const offerRef = useRef({presentation, token});
	offerRef.current = {presentation, token};
	const {open: openLogin} = useLoginModal();
	const {banner} = useMessages();
	const {showSuccessToast} = useContext(ToastContext);
	const refused = useRef(onRefused);
	refused.current = onRefused;
	useEffect(() => {
		if (inPage.offerRefused) refused.current();
	}, [inPage.offerRefused]);
	const withdrawn = presentation.kind === "coupon" && presentation.withdrawn;
	const subscribe = (readSelection?: () => CheckoutDocumentRead | undefined) => {
		if (inPage.busy || withdrawn || paymentAdmission(acknowledged) === "acknowledgementRequired") {
			return;
		}
		void resumeLimitWallCheckout({
			user: () => userRef.current, active: activeVisit, login: openLogin,
			checkout: () => limitWallCheckout(offerRef.current.presentation, offerRef.current.token),
			// The document read happens inside the start port, after any sign-in, for usePromoClaim's reason.
			start: async ({plan, period, terms}) => {
				const documentSelection = readSelection?.();
				await inPage.start(plan, period, {...terms, uiMode: "elements", immediatePerformanceConsent: acknowledged,
					...(documentSelection ? {documentSelection} : {})});
			},
		});
	};
	// Paid in place: the same confirming sentence useCouponClaim and usePromoClaim show for this moment.
	const paid = () => {
		inPage.completed();
		showSuccessToast(banner.firstMonthOffer.dialog.checkout.confirming);
		onClose();
	};
	return {subscribe, acknowledged, setAcknowledged, busy: inPage.busy, expired: withdrawn, previewStatus: "ready",
		amountDueNow: null, clientSecret: inPage.clientSecret, checkoutError: inPage.error, paid,
		dismissSheet: inPage.dismiss};
}
